/**
 * useResize – Hook for managing resize job lifecycle.
 *
 * Provides: startResize, poll status, cancel.
 * Polls every 2s while a job is active.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  startResize as apiStartResize,
  getResizeStatus,
  cancelResize as apiCancelResize,
  type ResizeStatusResponse,
} from "../lib/api.js";

export interface UseResizeReturn {
  /** Start a resize job. Returns true if queued successfully. */
  start: (recordId: string, driveLink: string, chatName: string) => Promise<boolean>;
  /** Cancel the running job. */
  cancel: () => Promise<void>;
  /** Current job status (null if no active job). */
  status: ResizeStatusResponse | null;
  /** True while waiting for initial start response. */
  starting: boolean;
  /** Error message from start or polling. */
  error: string | null;
}

const POLL_INTERVAL = 2_000;

export function useResize(): UseResizeReturn {
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<ResizeStatusResponse | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Poll when we have an active jobId
  useEffect(() => {
    if (!jobId) return;

    const poll = async () => {
      try {
        const s = await getResizeStatus(jobId);
        setStatus(s);
        if (s.status === "done" || s.status === "failed" || s.status === "cancelled") {
          // Stop polling
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      } catch (err) {
        // Job expired or network error — stop polling
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      }
    };

    // Initial poll
    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [jobId]);

  const start = useCallback(async (recordId: string, driveLink: string, chatName: string) => {
    setStarting(true);
    setError(null);
    setStatus(null);
    try {
      const res = await apiStartResize(recordId, driveLink, chatName);
      setJobId(res.jobId);
      setStarting(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start resize");
      setStarting(false);
      return false;
    }
  }, []);

  const cancel = useCallback(async () => {
    if (!jobId) return;
    try {
      await apiCancelResize(jobId);
    } catch {
      // Ignore cancel errors
    }
  }, [jobId]);

  return { start, cancel, status, starting, error };
}
